import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { clientesAPI, interaccionesAPI, archivosClienteAPI, facturasAPI, consultasAPI, authAPI, portalClienteAPI } from '../services/api';
import ClienteForm from './ClienteForm';
import { getLabels } from '../utils/userLabels';
import { getClienteColor } from '../utils/clienteColors';
import { exportToCSV } from '../utils/exportCSV';
import { getCurrencyConfig, formatMoney } from '../utils/currency';
import './Clientes.css';

// Función para limpiar HTML y mostrar solo texto
const stripHtml = (html) => {
  if (!html) return '';
  // Crear un elemento temporal para extraer el texto
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

function Clientes() {
  const location = useLocation();
  const navigate = useNavigate();
  const openedFromSearchRef = useRef(null);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [interacciones, setInteracciones] = useState([]);
  const [loadingInteracciones, setLoadingInteracciones] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showInteraccionForm, setShowInteraccionForm] = useState(false);
  const [user, setUser] = useState(null);
  const [formInteraccion, setFormInteraccion] = useState({
    tipo: 'llamada',
    titulo: '',
    descripcion: '',
    fecha_hora: new Date().toISOString().slice(0, 16),
    duracion_minutos: '',
    resultado: '',
  });

  // Tab activo en el detalle del cliente
  const [activeTab, setActiveTab] = useState('facturacion');

  // Sesiones del cliente
  const [sesionesCliente, setSesionesCliente] = useState([]);
  const [loadingSesiones, setLoadingSesiones] = useState(false);

  // Facturación del cliente
  const [facturasCliente, setFacturasCliente] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);

  // Archivos de cliente
  const [archivos, setArchivos] = useState([]);
  const [loadingArchivos, setLoadingArchivos] = useState(false);
  const [archivosCategoria, setArchivosCategoria] = useState('todos');
  const [uploadingArchivo, setUploadingArchivo] = useState(false);
  const [archivoNotas, setArchivoNotas] = useState('');
  const [archivoCategoria, setArchivoCategoria] = useState('otro');
  const fileInputRef = useRef(null);

  // Portal del cliente
  const [portalStatus, setPortalStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Labels dinámicos (Sesiones / Reuniones según tipo de usuario)
  const tipoUsuario = user?.tipo_usuario || 'coach';
  const labels = getLabels(tipoUsuario);
  const currency = getCurrencyConfig(user);

  useEffect(() => {
    loadClientes();
    // Cargar usuario para obtener módulos habilitados
    authAPI.check()
      .then(response => {
        if (response.data.authenticated && response.data.user) {
          setUser(response.data.user);
        }
      })
      .catch(err => {
        console.error('Error al cargar usuario:', err);
      });
  }, []);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await clientesAPI.getAll(false); // Cargar todas, incluyendo inactivas
      
      // Verificar que la respuesta es válida
      if (!response || !response.data) {
        console.error('Respuesta inválida:', response);
        setError('Error: La API no devolvió datos válidos');
        setClientes([]);
        return;
      }
      
      setClientes(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading clientes:', err.message);
      
      // Mostrar error más específico
      let errorMessage = 'Error al cargar los clientes. ';
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage += 'No estás autenticado. Por favor, inicia sesión de nuevo.';
        } else if (err.response.status === 500) {
          errorMessage += 'Error del servidor. Revisa los logs.';
        } else if (err.response.data?.error) {
          errorMessage += err.response.data.error;
        } else {
          errorMessage += `Error ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage += 'No se pudo conectar al servidor.';
      } else {
        errorMessage += err.message || 'Error desconocido';
      }
      
      setError(errorMessage);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCliente(null);
    setEditingCliente(null);
    setShowForm(true);
  };

  const handleEdit = (cliente) => {
    setSelectedCliente(null);
    setEditingCliente(cliente);
    setShowForm(true);
  };

  const handleDelete = async (id, activo) => {
    if (activo) {
      if (!window.confirm('¿Estás seguro de que quieres desactivar este cliente?')) {
        return;
      }
      try {
        await clientesAPI.delete(id, false); // desactivar (soft delete)
        setSuccess('Cliente desactivado correctamente');
        loadClientes();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Error al desactivar el cliente');
        console.error(err);
      }
    } else {
      // Reactivar cliente
      if (!window.confirm('¿Estás seguro de que quieres reactivar este cliente?')) {
        return;
      }
      try {
        // Obtener los datos actuales del cliente antes de actualizar
        const clienteActual = clientes.find(c => c.id === id);
        if (clienteActual) {
          // Actualizar solo el campo activo, manteniendo todos los demás datos
          await clientesAPI.update(id, { ...clienteActual, activo: 1 });
          setSuccess('Cliente reactivado correctamente');
          loadClientes();
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError('No se pudo encontrar el cliente para reactivar');
        }
      } catch (err) {
        setError('Error al reactivar el cliente');
        console.error(err);
      }
    }
  };

  const handleHardDelete = async (id) => {
    if (window.confirm('ATENCIÓN: Esto eliminará definitivamente el cliente y todas sus consultas, citas y documentos. ¿Seguro que quieres continuar?')) {
      try {
        await clientesAPI.delete(id, true); // eliminación definitiva
        setSuccess('Cliente eliminado definitivamente');
        loadClientes();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Error al eliminar definitivamente el cliente');
        console.error(err);
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCliente(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (editingCliente) {
        await clientesAPI.update(editingCliente.id, data);
        setSuccess('Cliente actualizado correctamente');
      } else {
        await clientesAPI.create(data);
        setSuccess('Cliente creado correctamente');
      }
      setShowForm(false);
      setEditingCliente(null);
      loadClientes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al guardar el cliente');
      console.error(err);
    }
  };

  // Función para filtrar interacciones según módulos habilitados
  const filtrarInteracciones = (interacciones) => {
    if (!user) return interacciones;
    
    return interacciones.filter(inter => {
      const tipo = inter.tipo?.toLowerCase();
      
      // Si es interacción de factura, verificar si facturación está habilitada
      if (tipo === 'factura') {
        return user.facturacion_habilitada === true || user.facturacion_habilitada === 1;
      }
      
      // Si es interacción de oportunidad, verificar si oportunidades están habilitadas
      if (tipo === 'oportunidad') {
        return user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1;
      }
      
      // Si es interacción de tarea, verificar si tareas están habilitadas
      if (tipo === 'tarea') {
        return user.tareas_habilitadas === true || user.tareas_habilitadas === 1;
      }
      
      // Para otros tipos (llamada, email, reunión, nota, cita, sesión, otro), siempre mostrar
      return true;
    });
  };

  const handleVerDetalle = async (cliente) => {
    setShowForm(false);
    setSelectedCliente(cliente);
    // Determinar tab por defecto (orden: Sesiones, Archivos, Facturación, Interacciones)
    setActiveTab('sesiones');

    // Cargar interacciones
    setLoadingInteracciones(true);
    try {
      const response = await interaccionesAPI.getByCliente(cliente.id, 100);
      const data = response.data?.data ?? response.data ?? [];
      const todasInteracciones = Array.isArray(data) ? data : [];
      const interaccionesFiltradas = filtrarInteracciones(todasInteracciones);
      setInteracciones(interaccionesFiltradas);
    } catch (err) {
      console.error('Error al cargar interacciones:', err);
      setInteracciones([]);
    } finally {
      setLoadingInteracciones(false);
    }
    // Cargar archivos del cliente
    loadArchivos(cliente.id);
    // Cargar facturas del cliente
    const hasFacturacion = user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1);
    if (hasFacturacion) loadFacturasCliente(cliente.id);
    // Cargar sesiones del cliente
    loadSesionesCliente(cliente.id);
    // Cargar estado del portal
    const hasPortal = user && (user.portal_cliente_habilitado === true || user.portal_cliente_habilitado === 1);
    if (hasPortal) {
      portalClienteAPI.getStatus(cliente.id)
        .then(r => setPortalStatus(r.data))
        .catch(() => setPortalStatus(null));
    } else {
      setPortalStatus(null);
    }
  };

  useEffect(() => {
    const openClienteId = location.state?.openClienteId;
    if (!openClienteId || clientes.length === 0) return;
    if (openedFromSearchRef.current === openClienteId) return;
    const cliente = clientes.find((c) => Number(c.id) === Number(openClienteId));
    if (!cliente) return;
    openedFromSearchRef.current = openClienteId;
    handleVerDetalle(cliente);
    navigate(location.pathname, { replace: true, state: {} });
    // Solo queremos reaccionar al state de navegación + datos cargados.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, clientes, navigate]);

  const loadSesionesCliente = async (clienteId) => {
    setLoadingSesiones(true);
    try {
      const response = await consultasAPI.getAll(clienteId);
      const data = response.data?.data ?? response.data ?? [];
      setSesionesCliente(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar sesiones:', err);
      setSesionesCliente([]);
    } finally {
      setLoadingSesiones(false);
    }
  };

  const loadFacturasCliente = async (clienteId) => {
    setLoadingFacturas(true);
    try {
      const response = await facturasAPI.getAll({ cliente_id: clienteId });
      const data = response.data?.data ?? response.data ?? [];
      setFacturasCliente(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar facturas:', err);
      setFacturasCliente([]);
    } finally {
      setLoadingFacturas(false);
    }
  };

  const loadArchivos = async (clienteId, categoria) => {
    setLoadingArchivos(true);
    try {
      const response = await archivosClienteAPI.getByCliente(clienteId, categoria);
      setArchivos(response.data?.data ?? []);
    } catch (err) {
      console.error('Error al cargar archivos:', err);
      setArchivos([]);
    } finally {
      setLoadingArchivos(false);
    }
  };

  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [archivoDragOver, setArchivoDragOver] = useState(false);

  const ARCHIVO_EXT_PERMITIDAS = new Set(['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.xls', '.xlsx', '.csv', '.txt']);
  const archivoExtensionValida = (file) => {
    if (!file?.name) return false;
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    return ARCHIVO_EXT_PERMITIDAS.has(ext);
  };

  const handleArchivoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadingArchivo) setArchivoDragOver(true);
  };
  const handleArchivoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setArchivoDragOver(false);
  };
  const handleArchivoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setArchivoDragOver(false);
    if (uploadingArchivo) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!archivoExtensionValida(file)) {
      setError('Formato no permitido. Use: PDF, DOC, DOCX, JPG, PNG, GIF, WEBP, XLS, XLSX, CSV, TXT');
      setTimeout(() => setError(null), 4000);
      return;
    }
    setArchivoSeleccionado(file);
    if (fileInputRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInputRef.current.files = dt.files;
    }
  };

  const handleUploadArchivo = async () => {
    if (!archivoSeleccionado || !selectedCliente) return;
    setUploadingArchivo(true);
    try {
      await archivosClienteAPI.upload(selectedCliente.id, archivoSeleccionado, archivoCategoria, archivoNotas);
      setSuccess('Archivo subido correctamente');
      // Registrar interacción
      try {
        await interaccionesAPI.create({
          cliente_id: selectedCliente.id,
          tipo: 'otro',
          titulo: 'Archivo subido: ' + archivoSeleccionado.name,
          descripcion: 'Categoría: ' + (CATEGORIAS.find(c => c.value === archivoCategoria)?.label || archivoCategoria) + (archivoNotas ? '. Notas: ' + archivoNotas : ''),
          fecha_hora: new Date().toISOString().slice(0, 16),
        });
        // Refrescar interacciones
        const resp = await interaccionesAPI.getByCliente(selectedCliente.id, 100);
        const d = resp.data?.data ?? resp.data ?? [];
        setInteracciones(Array.isArray(d) ? filtrarInteracciones(d) : []);
      } catch (_) { /* no bloquear si falla la interacción */ }
      setArchivoNotas('');
      setArchivoCategoria('otro');
      setArchivoSeleccionado(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadArchivos(selectedCliente.id, archivosCategoria !== 'todos' ? archivosCategoria : undefined);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al subir el archivo';
      setError(msg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploadingArchivo(false);
    }
  };

  const handleDeleteArchivo = async (archivoId, nombreOriginal) => {
    if (!window.confirm(`¿Eliminar el archivo "${nombreOriginal}"?`)) return;
    try {
      await archivosClienteAPI.delete(archivoId);
      setSuccess('Archivo eliminado');
      // Registrar interacción
      try {
        await interaccionesAPI.create({
          cliente_id: selectedCliente.id,
          tipo: 'otro',
          titulo: 'Archivo eliminado: ' + nombreOriginal,
          fecha_hora: new Date().toISOString().slice(0, 16),
        });
        const resp = await interaccionesAPI.getByCliente(selectedCliente.id, 100);
        const d = resp.data?.data ?? resp.data ?? [];
        setInteracciones(Array.isArray(d) ? filtrarInteracciones(d) : []);
      } catch (_) { /* no bloquear si falla la interacción */ }
      loadArchivos(selectedCliente.id, archivosCategoria !== 'todos' ? archivosCategoria : undefined);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al eliminar el archivo');
      setTimeout(() => setError(null), 5000);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mime) => {
    if (mime?.startsWith('image/')) return '🖼️';
    if (mime?.includes('pdf')) return '📄';
    if (mime?.includes('word') || mime?.includes('document')) return '📝';
    if (mime?.includes('sheet') || mime?.includes('excel') || mime?.includes('csv')) return '📊';
    if (mime?.includes('text')) return '📃';
    return '📎';
  };

  const CATEGORIAS = [
    { value: 'consentimiento', label: 'Consentimiento' },
    { value: 'contrato', label: 'Contrato' },
    { value: 'identificacion', label: 'Identificación' },
    { value: 'informe', label: 'Informe' },
    { value: 'seguimiento', label: 'Seguimiento' },
    { value: 'otro', label: 'Otro' },
  ];

  const formatCurrency = (amount) => {
    return formatMoney(amount, { ...currency, decimals: 2 });
  };

  const getEstadoFacturaColor = (estado) => {
    const colors = {
      borrador: '#95a5a6', pendiente: '#f39c12', enviada: '#3498db',
      pagada: '#27ae60', vencida: '#e74c3c', cancelada: '#7f8c8d'
    };
    return colors[estado] || '#95a5a6';
  };

  // Función para crear interacciones manualmente (futura funcionalidad)
  const handleNuevaInteraccion = async () => {
    if (!selectedCliente || !formInteraccion.titulo.trim()) {
      setError('El título es requerido');
      return;
    }
    try {
      await interaccionesAPI.create({
        cliente_id: selectedCliente.id,
        tipo: formInteraccion.tipo,
        titulo: formInteraccion.titulo,
        descripcion: formInteraccion.descripcion || null,
        fecha_hora: formInteraccion.fecha_hora,
        duracion_minutos: formInteraccion.duracion_minutos ? parseInt(formInteraccion.duracion_minutos) : null,
        resultado: formInteraccion.resultado || null,
      });
      const response = await interaccionesAPI.getByCliente(selectedCliente.id, 100);
      const data = response.data?.data ?? response.data ?? [];
      const todasInteracciones = Array.isArray(data) ? data : [];
      // Filtrar interacciones según módulos habilitados
      const interaccionesFiltradas = filtrarInteracciones(todasInteracciones);
      setInteracciones(interaccionesFiltradas);
      setSuccess('Interacción registrada');
      setShowInteraccionForm(false);
      setFormInteraccion({
        tipo: 'llamada',
        titulo: '',
        descripcion: '',
        fecha_hora: new Date().toISOString().slice(0, 16),
        duracion_minutos: '',
        resultado: '',
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al registrar interacción: ' + (err.response?.data?.error || err.message));
      console.error(err);
    }
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      llamada: '📞',
      email: '📧',
      reunion: '🤝',
      nota: '📝',
      tarea: '✅',
      cita: '📅',
      sesion: '💬',
      oportunidad: '💼',
      factura: '💰',
      otro: '📌'
    };
    return icons[tipo] || icons.otro;
  };

  if (loading) {
    return <div className="loading">Cargando clientes...</div>;
  }

  return (
    <div className="clientes">
      <div className="page-header">
        <h2>Gestión de Clientes</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => exportToCSV(clientes, [
            { key: 'nombre', label: 'Nombre' },
            { key: 'apellidos', label: 'Apellidos' },
            { key: 'email', label: 'Email' },
            { key: 'telefono', label: 'Teléfono' },
            { key: 'whatsapp', label: 'WhatsApp' },
            { key: 'ciudad', label: 'Ciudad' },
            { key: 'pais', label: 'País' },
            { key: 'modalidad_preferida', label: 'Modalidad' },
            { key: 'fecha_nacimiento', label: 'Fecha Nacimiento' },
            { label: 'Activo', format: (r) => r.activo ? 'Sí' : 'No' },
            { key: 'notas', label: 'Notas' },
            { key: 'created_at', label: 'Fecha Alta' },
          ], 'clientes')}>
            📥 Exportar CSV
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>
            Nuevo Cliente
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="clientes-grid">
        {clientes.map(cliente => (
          <div key={cliente.id} className={`cliente-card ${!cliente.activo ? 'inactiva' : ''}`}>
            <div className="cliente-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: getClienteColor(cliente.id, cliente.color),
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    flexShrink: 0,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }} title={`Color en calendario: ${getClienteColor(cliente.id, cliente.color)}`}></div>
                  <h3 style={{ margin: 0 }}>{cliente.nombre} {cliente.apellidos}</h3>
                </div>
                <p className="cliente-estado" style={{ marginTop: '5px' }}>
                  Estado: {cliente.activo ? '✅ Activo' : '❌ Inactivo'}
                </p>
              </div>
              <div className="cliente-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => handleVerDetalle(cliente)}>
                  Ver Detalle
                </button>
                <button className="btn btn-edit btn-sm" onClick={() => handleEdit(cliente)}>
                  Editar
                </button>
                <button 
                  className={cliente.activo ? "btn btn-danger btn-sm" : "btn btn-success btn-sm"} 
                  onClick={() => handleDelete(cliente.id, cliente.activo)}
                >
                  {cliente.activo ? 'Desactivar' : 'Reactivar'}
                </button>
                <button className="btn btn-danger btn-sm" style={{ marginLeft: '5px', backgroundColor: '#b30000' }} onClick={() => handleHardDelete(cliente.id)}>
                  Borrar
                </button>
                {(user?.rol === 'admin' || user?.whatsapp_habilitado === true || user?.whatsapp_habilitado === 1) && (cliente.whatsapp || cliente.telefono) && (
                  <button
                    className="btn btn-sm"
                    style={{ marginLeft: '5px', background: '#25D366', color: '#fff', border: 'none' }}
                    onClick={() => {
                      const phone = (cliente.whatsapp || cliente.telefono || '').replace(/[^0-9]/g, '');
                      const nombre = `${cliente.nombre || ''} ${cliente.apellidos || ''}`.trim();
                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Hola ${nombre}`)}`, '_blank');
                    }}
                    title="Contactar por WhatsApp"
                  >
                    WhatsApp
                  </button>
                )}
              </div>
            </div>
            <div className="cliente-info">
              {cliente.email && <p><strong>Email:</strong> {cliente.email}</p>}
              {cliente.telefono && <p><strong>Teléfono:</strong> {cliente.telefono}</p>}
              {cliente.whatsapp && <p><strong>WhatsApp:</strong> {cliente.whatsapp}</p>}
              {cliente.ciudad && <p><strong>Ciudad:</strong> {cliente.ciudad}</p>}
              {cliente.pais && <p><strong>País:</strong> {cliente.pais}</p>}
              {cliente.modalidad_preferida && (
                <p><strong>Modalidad Preferida:</strong> {cliente.modalidad_preferida}</p>
              )}
              {cliente.bienvenida && (
                <p><strong>Bienvenida:</strong> {cliente.bienvenida}</p>
              )}
              {cliente.tarjeta_cliente && (
                <p><strong>Tarjeta Cliente:</strong> {cliente.tarjeta_cliente}</p>
              )}
              {cliente.consideraciones_especiales && (
                <p><strong>Consideraciones Especiales:</strong> {cliente.consideraciones_especiales}</p>
              )}
              <p><strong>Consentimiento Comunicación Online:</strong> {cliente.consentimiento_comunicacion ? 'Sí' : 'No'}</p>
              <p><strong>Consentimiento Tratamiento Datos:</strong> {cliente.consentimiento_datos ? 'Sí' : 'No'}</p>
              {cliente.fecha_nacimiento && (
                <p><strong>Fecha de Nacimiento:</strong> {new Date(cliente.fecha_nacimiento).toLocaleDateString('es-ES')}</p>
              )}
              {cliente.lugar_nacimiento && (
                <p><strong>Lugar de Nacimiento:</strong> {cliente.lugar_nacimiento}</p>
              )}
              {cliente.objetivo && (
                <p><strong>Objetivo del proceso:</strong> {stripHtml(cliente.objetivo)}</p>
              )}
              {cliente.notas && (
                <p><strong>Notas:</strong> {stripHtml(cliente.notas)}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {clientes.length === 0 && (
        <div className="empty-state">
          <p>No hay clientes registrados. Crea el primero haciendo clic en "Nuevo Cliente".</p>
        </div>
      )}

      {showForm && createPortal(
        <ClienteForm
          cliente={editingCliente}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />,
        document.body
      )}

      {selectedCliente && !showForm && createPortal(
        <div
          className="clientes-modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}
          onClick={() => setSelectedCliente(null)}
        >
          <div
            className="clientes-modal-content cliente-detail"
            style={{ position: 'relative', zIndex: 3001, background: '#fff', width: '100%', maxWidth: '1400px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', padding: '24px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cliente-detail-header">
              <h2>{selectedCliente.nombre} {selectedCliente.apellidos}</h2>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-edit btn-sm" onClick={() => handleEdit(selectedCliente)}>
                  Editar
                </button>
                {(user?.rol === 'admin' || user?.whatsapp_habilitado === true || user?.whatsapp_habilitado === 1) && (selectedCliente.whatsapp || selectedCliente.telefono) && (
                  <button
                    className="btn btn-sm"
                    style={{ background: '#25D366', color: '#fff', border: 'none' }}
                    onClick={() => {
                      const phone = (selectedCliente.whatsapp || selectedCliente.telefono || '').replace(/[^0-9]/g, '');
                      const nombre = `${selectedCliente.nombre || ''} ${selectedCliente.apellidos || ''}`.trim();
                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Hola ${nombre}`)}`, '_blank');
                    }}
                    title="Contactar por WhatsApp"
                  >
                    WhatsApp
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedCliente(null)}>✕ Cerrar</button>
              </div>
            </div>
            
            <div className="cliente-detail-info">
              <div className="info-section">
                <h3>Información del Cliente</h3>
                {selectedCliente.email && <p><strong>Email:</strong> {selectedCliente.email}</p>}
                {selectedCliente.telefono && <p><strong>Teléfono:</strong> {selectedCliente.telefono}</p>}
                {selectedCliente.whatsapp && <p><strong>WhatsApp:</strong> {selectedCliente.whatsapp}</p>}
                {selectedCliente.ciudad && <p><strong>Ciudad:</strong> {selectedCliente.ciudad}</p>}
                {selectedCliente.fecha_nacimiento && (
                  <p><strong>Fecha de Nacimiento:</strong> {new Date(selectedCliente.fecha_nacimiento).toLocaleDateString('es-ES')}</p>
                )}
                {/* Última sesión */}
                {sesionesCliente.length > 0 && (
                  <p><strong>Última {labels.sesion_lower}:</strong> {new Date(sesionesCliente[0].fecha_consulta).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                )}
                {/* Última factura (solo si facturación habilitada) */}
                {user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && facturasCliente.length > 0 && (() => {
                  const ultimaFactura = [...facturasCliente].sort((a, b) => new Date(b.fecha_emision || 0) - new Date(a.fecha_emision || 0))[0];
                  return ultimaFactura ? (
                    <p><strong>Última factura:</strong> {ultimaFactura.fecha_emision ? new Date(ultimaFactura.fecha_emision).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'} — {formatCurrency(ultimaFactura.total)}</p>
                  ) : null;
                })()}
                {/* Portal del cliente */}
                {user && (user.portal_cliente_habilitado === true || user.portal_cliente_habilitado === 1) && (
                  <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e3f7' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <strong style={{ fontSize: '14px' }}>Portal del Cliente</strong>
                      <button
                        className={`btn btn-sm ${portalStatus?.portal_habilitado ? 'btn-success' : 'btn-secondary'}`}
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                        disabled={portalLoading}
                        onClick={async () => {
                          setPortalLoading(true);
                          try {
                            const r = await portalClienteAPI.toggle(selectedCliente.id);
                            setPortalStatus(r.data);
                          } catch (e) {
                            alert(e.response?.data?.error || 'Error al cambiar portal');
                          } finally {
                            setPortalLoading(false);
                          }
                        }}
                      >
                        {portalStatus?.portal_habilitado ? 'Activado' : 'Desactivado'}
                      </button>
                    </div>
                    {portalStatus?.portal_habilitado && portalStatus?.portal_url && (
                      <div style={{ fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <input
                            type="text"
                            readOnly
                            value={`${window.location.origin}${portalStatus.portal_url}`}
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', background: '#fff' }}
                            onClick={(e) => e.target.select()}
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            style={{ fontSize: '11px', padding: '4px 10px' }}
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}${portalStatus.portal_url}`);
                              alert('Enlace copiado al portapapeles');
                            }}
                          >
                            Copiar
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            style={{ fontSize: '11px', padding: '3px 8px' }}
                            onClick={() => window.open(portalStatus.portal_url, '_blank')}
                          >
                            Abrir portal
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            style={{ fontSize: '11px', padding: '3px 8px' }}
                            disabled={portalLoading}
                            onClick={async () => {
                              if (!window.confirm('¿Regenerar el enlace del portal? El enlace anterior dejará de funcionar.')) return;
                              setPortalLoading(true);
                              try {
                                const r = await portalClienteAPI.regenerateToken(selectedCliente.id);
                                setPortalStatus(prev => ({ ...prev, ...r.data }));
                              } catch (e) {
                                alert('Error al regenerar enlace');
                              } finally {
                                setPortalLoading(false);
                              }
                            }}
                          >
                            Regenerar enlace
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ====== TABS ====== */}
              <div className="detail-tabs">
                <div className="detail-tabs-nav">
                  <button
                    className={`detail-tab-btn ${activeTab === 'sesiones' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sesiones')}
                  >
                    📋 {labels.sesiones}
                  </button>
                  {user && (user.archivos_habilitados === true || user.archivos_habilitados === 1) && (
                    <button
                      className={`detail-tab-btn ${activeTab === 'archivos' ? 'active' : ''}`}
                      onClick={() => setActiveTab('archivos')}
                    >
                      📁 Archivos
                    </button>
                  )}
                  {user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
                    <button
                      className={`detail-tab-btn ${activeTab === 'facturacion' ? 'active' : ''}`}
                      onClick={() => { setActiveTab('facturacion'); if (facturasCliente.length === 0) loadFacturasCliente(selectedCliente.id); }}
                    >
                      💰 Facturación
                    </button>
                  )}
                  {user && (user.interacciones_habilitadas === true || user.interacciones_habilitadas === 1) && (
                    <button
                      className={`detail-tab-btn ${activeTab === 'interacciones' ? 'active' : ''}`}
                      onClick={() => setActiveTab('interacciones')}
                    >
                      💬 Interacciones
                    </button>
                  )}
                </div>
              </div>

              {/* ====== TAB: INTERACCIONES ====== */}
              {activeTab === 'interacciones' && user && (user.interacciones_habilitadas === true || user.interacciones_habilitadas === 1) && (
              <div className="timeline-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Historial de Interacciones</h3>
                  <button 
                    className="btn btn-primary btn-sm" 
                    onClick={() => setShowInteraccionForm(!showInteraccionForm)}
                  >
                    {showInteraccionForm ? '✕ Cancelar' : '+ Nueva Interacción'}
                  </button>
                </div>

                {showInteraccionForm && (
                  <div className="interaccion-form" style={{ 
                    background: '#f8f9fa', 
                    padding: '15px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Nueva Interacción</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <label>Tipo *</label>
                        <select 
                          value={formInteraccion.tipo} 
                          onChange={(e) => setFormInteraccion(f => ({ ...f, tipo: e.target.value }))}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="llamada">Llamada</option>
                          <option value="email">Email</option>
                          <option value="reunion">Reunión</option>
                          <option value="nota">Nota</option>
                          {user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
                            <option value="factura">Factura</option>
                          )}
                          {user && (user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1) && (
                            <option value="oportunidad">Oportunidad</option>
                          )}
                          {user && (user.tareas_habilitadas === true || user.tareas_habilitadas === 1) && (
                            <option value="tarea">Tarea</option>
                          )}
                          <option value="cita">Cita</option>
                          <option value="sesion">Sesión</option>
                          <option value="otro">Otro</option>
                        </select>
                      </div>
                      <div>
                        <label>Fecha y Hora *</label>
                        <input 
                          type="datetime-local" 
                          value={formInteraccion.fecha_hora} 
                          onChange={(e) => setFormInteraccion(f => ({ ...f, fecha_hora: e.target.value }))}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                      <label>Título *</label>
                      <input 
                        type="text" 
                        value={formInteraccion.titulo} 
                        onChange={(e) => setFormInteraccion(f => ({ ...f, titulo: e.target.value }))}
                        placeholder="Ej: Llamada de seguimiento"
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div style={{ marginTop: '15px' }}>
                      <label>Descripción</label>
                      <textarea 
                        value={formInteraccion.descripcion} 
                        onChange={(e) => setFormInteraccion(f => ({ ...f, descripcion: e.target.value }))}
                        rows={3}
                        placeholder="Detalles de la interacción..."
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                      <div>
                        <label>Duración (minutos)</label>
                        <input 
                          type="number" 
                          value={formInteraccion.duracion_minutos} 
                          onChange={(e) => setFormInteraccion(f => ({ ...f, duracion_minutos: e.target.value }))}
                          min="0"
                          placeholder="Opcional"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                      <div>
                        <label>Resultado</label>
                        <input 
                          type="text" 
                          value={formInteraccion.resultado} 
                          onChange={(e) => setFormInteraccion(f => ({ ...f, resultado: e.target.value }))}
                          placeholder="Opcional"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button 
                        className="btn btn-secondary btn-sm" 
                        onClick={() => {
                          setShowInteraccionForm(false);
                          setFormInteraccion({
                            tipo: 'llamada',
                            titulo: '',
                            descripcion: '',
                            fecha_hora: new Date().toISOString().slice(0, 16),
                            duracion_minutos: '',
                            resultado: '',
                          });
                        }}
                      >
                        Cancelar
                      </button>
                      <button 
                        className="btn btn-primary btn-sm" 
                        onClick={handleNuevaInteraccion}
                      >
                        Guardar Interacción
                      </button>
                    </div>
                  </div>
                )}

                {loadingInteracciones ? (
                  <p>Cargando...</p>
                ) : interacciones.length === 0 ? (
                  <p className="empty-msg">No hay interacciones registradas</p>
                ) : (
                  <div className="timeline">
                    {interacciones.map((inter) => (
                      <div key={inter.id} className="timeline-item">
                        <div className="timeline-marker">
                          <span className="timeline-icon">{getTipoIcon(inter.tipo)}</span>
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <strong>{inter.titulo}</strong>
                            <span className="timeline-date">
                              {new Date(inter.fecha_hora).toLocaleString('es-ES')}
                            </span>
                          </div>
                          <div className="timeline-type">{inter.tipo}</div>
                          {inter.descripcion && <p className="timeline-desc">{inter.descripcion}</p>}
                          {inter.duracion_minutos && (
                            <span className="timeline-duration">Duración: {inter.duracion_minutos} min</span>
                          )}
                          {inter.resultado && (
                            <div className="timeline-resultado">
                              <strong>Resultado:</strong> {inter.resultado}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* ====== TAB: ARCHIVOS ====== */}
              {activeTab === 'archivos' && user && (user.archivos_habilitados === true || user.archivos_habilitados === 1) && (
              <div className="archivos-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>📁 Archivos del Cliente</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={archivosCategoria}
                      onChange={(e) => {
                        setArchivosCategoria(e.target.value);
                        loadArchivos(selectedCliente.id, e.target.value !== 'todos' ? e.target.value : undefined);
                      }}
                      style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
                    >
                      <option value="todos">Todas las categorías</option>
                      {CATEGORIAS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Formulario de subida (con drag & drop) */}
                <div
                  className={`archivo-upload-form${archivoDragOver ? ' drag-over' : ''}`}
                  onDragOver={handleArchivoDragOver}
                  onDragLeave={handleArchivoDragLeave}
                  onDrop={handleArchivoDrop}
                >
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Archivo</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={(e) => setArchivoSeleccionado(e.target.files?.[0] || null)}
                        disabled={uploadingArchivo}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.xls,.xlsx,.csv,.txt"
                        style={{ fontSize: '13px', width: '100%' }}
                      />
                    </div>
                    <div style={{ minWidth: '130px' }}>
                      <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Categoría</label>
                      <select
                        value={archivoCategoria}
                        onChange={(e) => setArchivoCategoria(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', width: '100%' }}
                      >
                        {CATEGORIAS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Notas (opcional)</label>
                      <input
                        type="text"
                        value={archivoNotas}
                        onChange={(e) => setArchivoNotas(e.target.value)}
                        placeholder="Descripción breve..."
                        style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', width: '100%' }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleUploadArchivo}
                      disabled={!archivoSeleccionado || uploadingArchivo}
                      style={{ padding: '7px 18px', fontSize: '13px' }}
                    >
                      {uploadingArchivo ? 'Subiendo...' : '📤 Subir archivo'}
                    </button>
                    {archivoSeleccionado && (
                      <span style={{ fontSize: '12px', color: '#555' }}>
                        {archivoSeleccionado.name} ({formatFileSize(archivoSeleccionado.size)})
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '11px', color: '#999' }}>
                    Arrastra un archivo aquí o elige desde el selector. Máx. 10 MB. Formatos: PDF, DOC, DOCX, JPG, PNG, GIF, WEBP, XLS, XLSX, CSV, TXT
                  </div>
                </div>

                {/* Lista de archivos */}
                {loadingArchivos ? (
                  <p style={{ color: '#888', fontSize: '14px' }}>Cargando archivos...</p>
                ) : archivos.length === 0 ? (
                  <p className="empty-msg" style={{ marginTop: '15px' }}>No hay archivos para este cliente</p>
                ) : (
                  <div className="archivos-lista">
                    {archivos.map(archivo => (
                      <div key={archivo.id} className="archivo-item">
                        <div className="archivo-icon">{getFileIcon(archivo.tipo_mime)}</div>
                        <div className="archivo-info">
                          <div className="archivo-nombre">{archivo.nombre_original}</div>
                          <div className="archivo-meta">
                            <span className="archivo-categoria">{CATEGORIAS.find(c => c.value === archivo.categoria)?.label || archivo.categoria}</span>
                            <span>{formatFileSize(archivo.tamano)}</span>
                            <span>{new Date(archivo.fecha_subida).toLocaleDateString('es-ES')}</span>
                          </div>
                          {archivo.notas && <div className="archivo-notas">{archivo.notas}</div>}
                        </div>
                        <div className="archivo-actions">
                          <button
                            className="btn btn-sm"
                            onClick={() => archivosClienteAPI.download(archivo.id)}
                            title="Descargar"
                            style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}
                          >
                            ⬇️
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => handleDeleteArchivo(archivo.id, archivo.nombre_original)}
                            title="Eliminar"
                            style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: '#e74c3c' }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* ====== TAB: SESIONES / REUNIONES ====== */}
              {activeTab === 'sesiones' && (
              <div className="sesiones-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Resumen de {labels.sesiones}</h3>
                </div>
                {loadingSesiones ? (
                  <p style={{ color: '#888', fontSize: '14px' }}>Cargando {labels.sesiones_lower}...</p>
                ) : sesionesCliente.length === 0 ? (
                  <p className="empty-msg">No hay {labels.sesiones_lower} registradas para este cliente</p>
                ) : (
                  <>
                    {/* KPIs */}
                    <div className="sesiones-resumen-kpis" style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                      <div style={{ background: '#f0f7ff', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Total {labels.sesiones_lower}</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>{sesionesCliente.length}</div>
                      </div>
                      <div style={{ background: '#f0fff4', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Horas completadas</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#27ae60' }}>
                          {(sesionesCliente.reduce((sum, s) => sum + (parseInt(s.duracion_minutos) || 0), 0) / 60).toFixed(1)}h
                        </div>
                      </div>
                      <div style={{ background: '#fff8f0', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Última {labels.sesion_lower}</div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#e67e22' }}>
                          {sesionesCliente[0]?.fecha_consulta
                            ? new Date(sesionesCliente[0].fecha_consulta).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
                            : '-'}
                        </div>
                      </div>
                      <div style={{ background: '#f5f0ff', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Modalidad más usada</div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#8e44ad' }}>
                          {(() => {
                            const mods = {};
                            sesionesCliente.forEach(s => { const m = s.modalidad || 'Sin definir'; mods[m] = (mods[m] || 0) + 1; });
                            return Object.entries(mods).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Tabla de sesiones */}
                    <div className="sesiones-lista">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Nº</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Fecha</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Modalidad</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Duración</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Objetivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sesionesCliente.map(sesion => (
                            <tr key={sesion.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '10px', fontWeight: '500' }}>#{sesion.numero_sesion || '-'}</td>
                              <td style={{ padding: '10px' }}>
                                {sesion.fecha_consulta
                                  ? new Date(sesion.fecha_consulta).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
                                  : '-'}
                              </td>
                              <td style={{ padding: '10px' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 10px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: sesion.modalidad === 'Online' ? '#e8f4fd' : sesion.modalidad === 'Presencial' ? '#e8fde8' : '#f5f5f5',
                                  color: sesion.modalidad === 'Online' ? '#2980b9' : sesion.modalidad === 'Presencial' ? '#27ae60' : '#666'
                                }}>
                                  {sesion.modalidad || 'Sin definir'}
                                </span>
                              </td>
                              <td style={{ padding: '10px' }}>
                                {sesion.duracion_minutos ? `${sesion.duracion_minutos} min` : '-'}
                              </td>
                              <td style={{ padding: '10px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {sesion.objetivos ? stripHtml(sesion.objetivos).substring(0, 80) + (stripHtml(sesion.objetivos).length > 80 ? '...' : '') : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              )}

              {/* ====== TAB: FACTURACIÓN ====== */}
              {activeTab === 'facturacion' && user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
              <div className="facturacion-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Facturas del Cliente</h3>
                </div>
                {loadingFacturas ? (
                  <p style={{ color: '#888', fontSize: '14px' }}>Cargando facturas...</p>
                ) : facturasCliente.length === 0 ? (
                  <p className="empty-msg">No hay facturas para este cliente</p>
                ) : (
                  <>
                    <div className="facturas-resumen" style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                      <div style={{ background: '#f0f7ff', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Total facturado</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                          {formatCurrency(facturasCliente.reduce((sum, f) => sum + parseFloat(f.total || 0), 0))}
                        </div>
                      </div>
                      <div style={{ background: '#f0fff4', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Cobrado</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#27ae60' }}>
                          {formatCurrency(facturasCliente.filter(f => f.estado === 'pagada').reduce((sum, f) => sum + parseFloat(f.total || 0), 0))}
                        </div>
                      </div>
                      <div style={{ background: '#fff8f0', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Pendiente</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#f39c12' }}>
                          {formatCurrency(facturasCliente.filter(f => f.estado !== 'pagada' && f.estado !== 'cancelada').reduce((sum, f) => sum + parseFloat(f.total || 0), 0))}
                        </div>
                      </div>
                      <div style={{ background: '#f5f5f5', padding: '12px 18px', borderRadius: '8px', flex: '1', minWidth: '120px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Nº Facturas</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>{facturasCliente.length}</div>
                      </div>
                    </div>
                    <div className="facturas-lista">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Nº</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Fecha</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500' }}>Concepto</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500', textAlign: 'right' }}>Total</th>
                            <th style={{ padding: '8px 10px', color: '#666', fontWeight: '500', textAlign: 'center' }}>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {facturasCliente.map(factura => (
                            <tr key={factura.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '10px' }}>{factura.numero_factura || '-'}</td>
                              <td style={{ padding: '10px' }}>{factura.fecha_emision ? new Date(factura.fecha_emision).toLocaleDateString('es-ES') : '-'}</td>
                              <td style={{ padding: '10px' }}>{factura.concepto || factura.notas || '-'}</td>
                              <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>{formatCurrency(factura.total)}</td>
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 10px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  color: '#fff',
                                  background: getEstadoFacturaColor(factura.estado)
                                }}>
                                  {factura.estado ? factura.estado.charAt(0).toUpperCase() + factura.estado.slice(1) : '-'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              )}

            </div>
          </div>
        </div>
        ,
        document.body
      )}
    </div>
  );
}

export default Clientes;

